/**
 * Per-session localStorage persistence for epi-chat transcripts.
 *
 * We maintain one ``EpiChatState`` per session in memory (see
 * {@link useEpiChatState}). This hook is the bridge to disk:
 *   - on first touch of a session id (whether focused or just receiving
 *     background events) we hydrate from localStorage if a snapshot exists,
 *     otherwise we reset to initial state; and
 *   - on every subsequent state change, we write the relevant session's
 *     snapshot back under the same key.
 *
 * What is NOT persisted: ``runState``, ``runId``, ``queue``, and ``error``.
 * Any streaming message or tool call still open at persist time is closed out
 * on hydrate (see the ``hydrated`` reducer).
 */

import { useEffect, useRef } from 'react';

import type { EpiChatStateBySession } from './useEpiChatState';
import type { EpiChatDispatchTo } from './useEpiChatStream';
import { epiChatActions } from '../state/epiChatSlice';
import type { EpiArtifact, EpiArtifactTab, EpiMessage } from '../types/epi-chat-types';

const KEY_PREFIX = 'vep.epi-study-agent.transcript.v1.';
const storageKey = (sessionId: string) => `${KEY_PREFIX}${sessionId}`;

/** Hard cap on the number of session transcripts cached locally per browser.
 *  S3 is the canonical store — localStorage is only a snappy-rehydration
 *  cache for the most-recently-viewed sessions. Capping prevents the
 *  ``QuotaExceededError`` lockout where Auth0 couldn't write its JWT
 *  because the chat backlog had eaten the per-origin storage budget. */
const MAX_CACHED_TRANSCRIPTS = 5;

interface StoredTranscript {
  messages: EpiMessage[];
  artifacts: EpiArtifact[];
  activeArtifactId: string | null;
  activeArtifactTab?: EpiArtifactTab | null;
  lastEventSeq?: number;
  /** Wall-clock when this transcript was last hydrated or rewritten. Used
   *  to evict the least-recently-viewed sessions when the cache exceeds
   *  ``MAX_CACHED_TRANSCRIPTS``. Older snapshots without this field sort
   *  to the end of the LRU list and are evicted first. */
  lastAccessedAt?: string;
}

const readTranscript = (sessionId: string): StoredTranscript | null => {
  try {
    const raw = window.localStorage.getItem(storageKey(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredTranscript;
    if (!parsed || !Array.isArray(parsed.messages) || !Array.isArray(parsed.artifacts)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Drop every stored transcript except the one we're writing. Used as a
 * last-ditch recovery when ``setItem`` throws ``QuotaExceededError`` —
 * which has been observed locking users out of Auth0 on the same origin
 * (the JWT response can't fit). Transcripts are a snappy-rehydration
 * cache; the server is source of truth, so wholesale eviction is safe —
 * the next subscribe rebuilds from the per-session ring buffer.
 */
const evictAllTranscriptsExcept = (keepKey: string): void => {
  const toDrop: string[] = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const k = window.localStorage.key(i);
    if (k && k.startsWith(KEY_PREFIX) && k !== keepKey) {
      toDrop.push(k);
    }
  }
  for (const k of toDrop) {
    try {
      window.localStorage.removeItem(k);
    } catch {
      // ignore — best-effort
    }
  }
};

/**
 * Keep only the ``MAX_CACHED_TRANSCRIPTS`` most-recently-accessed
 * transcripts. ``keepKey`` always survives — it's the one we just wrote.
 * Order is by ``lastAccessedAt`` on the stored payload, descending; missing
 * timestamps sort last (legacy entries get evicted first).
 *
 * Rationale: localStorage has a per-origin quota (typically 5-10 MB) that
 * Auth0 also writes into. A long-running user accumulating dozens of
 * sessions will eventually crowd Auth0 out and lock themselves into a
 * login loop. S3 is the source of truth; eviction here only loses the
 * snappy-rehydration cache. The next time the user opens an evicted
 * session, the backend's ``session_replay`` synth fills it back in.
 */
const enforceCacheCap = (keepKey: string): void => {
  type Entry = { key: string; lastAccessedAt: number };
  const entries: Entry[] = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const k = window.localStorage.key(i);
    if (!k || !k.startsWith(KEY_PREFIX)) continue;
    let lastAccessedAt = 0;
    try {
      const raw = window.localStorage.getItem(k);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<StoredTranscript>;
        const ts = parsed.lastAccessedAt;
        if (typeof ts === 'string') {
          const parsedDate = Date.parse(ts);
          if (!Number.isNaN(parsedDate)) lastAccessedAt = parsedDate;
        }
      }
    } catch {
      // Corrupt entry — leave at lastAccessedAt=0 so it's evicted first.
    }
    entries.push({ key: k, lastAccessedAt });
  }
  if (entries.length <= MAX_CACHED_TRANSCRIPTS) return;
  // Newest first; ``keepKey`` is hard-pinned to position 0 so a write
  // that races a slightly-older sibling never evicts itself.
  entries.sort((a, b) => {
    if (a.key === keepKey) return -1;
    if (b.key === keepKey) return 1;
    return b.lastAccessedAt - a.lastAccessedAt;
  });
  for (const entry of entries.slice(MAX_CACHED_TRANSCRIPTS)) {
    try {
      window.localStorage.removeItem(entry.key);
    } catch {
      // best-effort
    }
  }
};

const writeTranscript = (sessionId: string, snapshot: StoredTranscript) => {
  const key = storageKey(sessionId);
  const stamped: StoredTranscript = {
    ...snapshot,
    lastAccessedAt: new Date().toISOString(),
  };
  const body = JSON.stringify(stamped);
  try {
    window.localStorage.setItem(key, body);
  } catch (err) {
    // Quota or disabled storage. If this is a quota error, free room by
    // dropping every OTHER transcript and retry once. Two transcripts
    // ago this user could log in; today they can't, because Auth0's
    // setItem now collides with our backlog. Self-heal so the next
    // refresh isn't dependent on a manual DevTools sweep.
    const isQuota =
      err instanceof DOMException &&
      (err.name === 'QuotaExceededError' || err.code === 22 || err.code === 1014);
    if (!isQuota) return;
    evictAllTranscriptsExcept(key);
    try {
      window.localStorage.setItem(key, body);
    } catch {
      // Still no room (this transcript alone exceeds quota). Give up
      // silently — server-side state is intact and the next subscribe
      // will rebuild from the event ring buffer.
      return;
    }
  }
  // Steady-state cap enforcement. Runs after every write so the cache
  // never grows unbounded — sets a hard ceiling on disk regardless of
  // how many sessions the user opens. Cheap (one localStorage scan over
  // 5–N short keys) and deterministic.
  enforceCacheCap(key);
};

export const removeStoredTranscript = (sessionId: string) => {
  try {
    window.localStorage.removeItem(storageKey(sessionId));
  } catch {
    // best-effort
  }
};

export const useEpiChatPersistence = (
  activeSessionId: string | null,
  stateBySession: EpiChatStateBySession,
  dispatchTo: EpiChatDispatchTo
): void => {
  // Tracks which session ids we've already hydrated this tab. A hydrate
  // only runs once per session id — after that the in-memory map is the
  // source of truth and writes cascade to localStorage.
  const hydratedRef = useRef<Set<string>>(new Set());

  // Hydrate the active session on focus change. Background sessions
  // (receiving events while not focused) are hydrated lazily by the
  // same guard below when their first event lands.
  useEffect(() => {
    if (!activeSessionId) return;
    if (hydratedRef.current.has(activeSessionId)) return;
    const stored = readTranscript(activeSessionId);
    if (stored) {
      dispatchTo(
        activeSessionId,
        epiChatActions.hydrated({
          sessionId: activeSessionId,
          messages: stored.messages,
          artifacts: stored.artifacts,
          activeArtifactId: stored.activeArtifactId,
          activeArtifactTab: stored.activeArtifactTab,
          lastEventSeq: stored.lastEventSeq,
        })
      );
    } else {
      dispatchTo(activeSessionId, epiChatActions.reset());
    }
    hydratedRef.current.add(activeSessionId);
  }, [activeSessionId, dispatchTo]);

  // Persist every session's snapshot whose state has changed since the last
  // write. The active session persists naturally; background sessions do too
  // (that's the whole point — welcome text streaming into session A while B
  // is focused still lands in A's on-disk transcript).
  const lastPersistedRef = useRef<Map<string, EpiChatStateBySession[string]>>(new Map());
  useEffect(() => {
    for (const [sid, sessionState] of Object.entries(stateBySession)) {
      if (!hydratedRef.current.has(sid)) {
        // Hasn't been hydrated yet — don't persist an empty in-memory slot
        // over a real on-disk transcript. The active-session effect above
        // (or the focus flip) will hydrate first.
        continue;
      }
      if (lastPersistedRef.current.get(sid) === sessionState) continue;
      writeTranscript(sid, {
        messages: sessionState.messages,
        artifacts: sessionState.artifacts,
        activeArtifactId: sessionState.activeArtifactId,
        activeArtifactTab: sessionState.activeArtifactTab,
        lastEventSeq: sessionState.lastEventSeq,
      });
      lastPersistedRef.current.set(sid, sessionState);
    }
  }, [stateBySession]);
};

/** Exposed so the page hook can hydrate a non-active session on-demand. */
export const hydrateSessionFromStorage = (
  sessionId: string,
  dispatchTo: EpiChatDispatchTo
): void => {
  const stored = readTranscript(sessionId);
  if (stored) {
    dispatchTo(
      sessionId,
      epiChatActions.hydrated({
        sessionId,
        messages: stored.messages,
        artifacts: stored.artifacts,
        activeArtifactId: stored.activeArtifactId,
        activeArtifactTab: stored.activeArtifactTab,
        lastEventSeq: stored.lastEventSeq,
      })
    );
  } else {
    dispatchTo(sessionId, epiChatActions.reset());
  }
};
